import {StockItem, StockItemUpdateFieldValue, StockItemUpdateObj} from '../../../shared-utilities/models-old/datastructures';
import {
  IDisableStockItemRule,
  IEnableStockItemRule,
  normalizeDisableStockItemRuleFromFirestore,
} from '../../settings-new/models';
import {ReportUtils} from '../../../shared-utilities/utils-old/shared-utils-old/report-utils';

const DISABLE_AFFECTED_FIELDS: string[] = ['desc', 'lineColourCode', 'onHoldCode', 'regularSuppCode'];

/**
 * Order grid rows are not stock manager rows, so they carry no originalValue snapshot.
 * The disable rule helpers read that snapshot to decide what an enable restores, so build
 * one from the values the item held before it was disabled in this session. Without a
 * baseline the row is taken as loaded, which is how an item that arrived already disabled
 * is treated: the enable falls back to the store's configured enabled values.
 */
function withOriginalValueSnapshot(item: StockItem, baseline: Record<string, StockItemUpdateFieldValue> | undefined): StockItem {
  const tags: string[] = item._tags ?? [];
  const snapshot: Record<string, {value: unknown}> = {
    _tags: {value: baseline ? tags.filter((tag: string): boolean => tag !== 'disabled') : tags},
  };
  DISABLE_AFFECTED_FIELDS.forEach((field: string): void => {
    snapshot[field] = {value: baseline?.[field] ?? (item as unknown as Record<string, unknown>)[field]};
  });

  return {
    ...item,
    originalValue: snapshot,
  } as unknown as StockItem;
}

/**
 * Applies the store's stock item disabling rules to a single order row and returns the
 * resulting stock update payload. Returns null when the rules change nothing on the item.
 * Pass the values captured when the item was disabled so a later enable restores those
 * rather than the disabled values now sitting on the row.
 */
export function buildDisableStockItemUpdate(
  item: StockItem,
  disableRules: IDisableStockItemRule | undefined,
  enableRules: IEnableStockItemRule | undefined,
  enable: boolean,
  baseline?: Record<string, StockItemUpdateFieldValue>,
): Record<string, StockItemUpdateObj> | null {
  if (!item?.code || !disableRules) {
    return null;
  }

  const before: StockItem = withOriginalValueSnapshot(item, baseline);
  const after: StockItem = withOriginalValueSnapshot(item, baseline);
  const normalizedDisableRules: IDisableStockItemRule = normalizeDisableStockItemRuleFromFirestore(disableRules);

  ReportUtils.enableReport(after, normalizedDisableRules, enableRules ?? {} as IEnableStockItemRule, enable, {
    onHoldCode: Number(baseline?.['onHoldCode'] ?? item.onHoldCode ?? 0),
    lineColourCode: Number(baseline?.['lineColourCode'] ?? item.lineColourCode ?? 0),
  });

  const oldValues: Record<string, StockItemUpdateFieldValue> = {};
  const newValues: Record<string, StockItemUpdateFieldValue> = {};

  DISABLE_AFFECTED_FIELDS.forEach((field: string): void => {
    const previous: unknown = (before as unknown as Record<string, unknown>)[field];
    const next: unknown = (after as unknown as Record<string, unknown>)[field];
    if (previous === next) {
      return;
    }
    oldValues[field] = (previous ?? '') as StockItemUpdateFieldValue;
    newValues[field] = (next ?? '') as StockItemUpdateFieldValue;
  });

  if (Object.keys(newValues).length === 0) {
    return null;
  }

  return {
    [item.code]: {
      o: oldValues,
      n: newValues,
      details: {[1]: before.desc ?? ''},
    },
  };
}
